import type { Mesh } from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

import type { GeometryBuilder } from '../geometry-builder.class';
import type { AddPanelInfo } from '../../../../model/add-panel-info';
import { getActualQuality, applyModelQualityOptimizations } from '../helpers';

export interface LoadModelResult {
	generateFromModel: boolean;
	model: Mesh | null;
}

/**
 * Find first Mesh in object hierarchy
 */
const findMeshInObject = (obj: any): Mesh | null => {
	if (obj.type === 'Mesh') {
		return obj;
	}

	if (obj.type === 'Group' || obj.type === 'Object3D') {
		for (const child of obj.children) {
			const mesh = findMeshInObject(child);
			if (mesh) return mesh;
		}
	}

	return null;
};

/**
 * Load GLTF model if modelPath is specified
 */
export const loadModelIfNeededCfn = async (
	self: GeometryBuilder,
	{ api }: { api: AddPanelInfo }
): Promise<LoadModelResult> => {
	if (!api.modelPath) {
		return { generateFromModel: false, model: null };
	}

	try {
		let meshToClone: Mesh;

		// Check cache first
		if (self.gltfModelCache.has(api.modelPath)) {
			meshToClone = self.gltfModelCache.get(api.modelPath)!;
		} else {
			// Load model
			const url = `${self.configAccess.basePath}models/${api.modelPath}`;
			const loader = new GLTFLoader();
			const gltf = await loader.loadAsync(url);

			const modelFromScene = gltf.scene.children[0];
			meshToClone = findMeshInObject(modelFromScene) as Mesh;

			// Cache for reuse
			self.gltfModelCache.set(api.modelPath, meshToClone);
		}

		// Clone model
		const model = meshToClone.clone();
		model.scale.set(1, 1, 1);

		// Apply rotation
		if (api.rotation) {
			model.rotateX((api.rotation.x || 0) * Math.PI / 180);
			model.rotateY((api.rotation.y || 0) * Math.PI / 180);
			model.rotateZ((api.rotation.z || 0) * Math.PI / 180);
		}

		// Apply position
		if (api.center) {
			const depth = api.size?.depth || 1;
			model.position.x = api.center.x - ((depth / 2) * Math.sin((api.rotation?.y || 0) * Math.PI / 180));
			model.position.y = api.center.y;
			model.position.z = api.center.z - ((depth / 2) * Math.cos((api.rotation?.y || 0) * Math.PI / 180));
		}

		// Apply quality optimizations
		const actualQuality = getActualQuality(self.configAccess.renderQuality);
		applyModelQualityOptimizations(model, actualQuality);

		return { generateFromModel: true, model };
	} catch (error) {
		console.error('GeometryBuilder: Error loading model:', error);
		return { generateFromModel: false, model: null };
	}
};
